import React from 'react';
import { CurrencyDollarIcon } from '@heroicons/react/20/solid';

const PaymentSummary = ({
  selectedServicesByEmployee,
  employees,
  paymentMethods,
  paymentMethod,
  sameDayTurnos,
  giftCard,
  giftCardAmount,
  monto, // Abono (Sena) para el turno actual
}) => {
  // Calcular subtotal de servicios
  const totalAmount = Object.values(selectedServicesByEmployee)
    .flat()
    .reduce((acc, service) => acc + (service.precio || 0) * service.count, 0);

  // Determinar recargo (si corresponde)
  const selectedPaymentMethod = paymentMethods.find(method => method.nombre === paymentMethod);
  const surcharge = selectedPaymentMethod?.tiene_recargo
    ? (totalAmount * selectedPaymentMethod.valor_recargo) / 100
    : 0;
  const totalWithSurcharge = totalAmount + surcharge;

  // Sumar el Sena de otros turnos del mismo día
  const totalSameDaySena = sameDayTurnos.reduce((acc, turno) => acc + (turno.sena_monto || 0), 0);

  // Restar giftCardAmount, Sena de otros turnos y el abono (monto) del turno actual
  const rawTotal = totalWithSurcharge - (giftCardAmount || 0) - totalSameDaySena - (monto || 0);
  const totalFinal = Math.max(rawTotal, 0);

  return (
    <div className="space-y-8 text-gray-800">
      {/* Servicios por empleado */}
      {Object.keys(selectedServicesByEmployee).map((employeeId) => {
        const employee = employees.find((emp) => emp.empleado_id === employeeId);
        const services = selectedServicesByEmployee[employeeId] || [];
        if (services.length === 0) return null;
  
        return (
          <div key={employeeId} className="bg-white border border-gray-300 rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Servicios de: {employee?.nombre || 'Empleado'}
            </h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.servicio_id} className="flex justify-between items-center">
                  <span className="text-base">{service.nombre} x {service.count}</span>
                  <span className="text-base font-semibold">${(service.precio * service.count).toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
  
      {/* Gift Card aplicada */}
      {giftCard && (
        <div className="bg-green-100 border border-green-300 rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-bold text-green-900 mb-2">
            <i className="fas fa-gift mr-2 text-green-700" />Gift Card Aplicada
          </h3>
          <div className="text-base space-y-1 text-green-800">
            <p>Código: <span className="font-semibold">{giftCard.public_code}</span></p>
            <p>Saldo disponible: <span className="font-semibold">${giftCard.current_balance.toFixed(2)}</span></p>
            <p>Válida hasta: <span className="font-semibold">{new Date(giftCard.expires_at).toLocaleDateString()}</span></p>
          </div>
        </div>
      )}
  
      {/* Otros turnos del mismo día */}
      {sameDayTurnos.length > 0 && (
        <div className="bg-yellow-100 border border-yellow-300 rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-bold text-yellow-900 mb-3">
            <i className="fas fa-calendar-day mr-2 text-yellow-800" />Otros turnos del día
          </h3>
          <ul className="space-y-4 text-yellow-900">
            {sameDayTurnos.map((turno) => (
              <li key={turno.turno_id}>
                <div className="text-base font-semibold">
                  {turno.hora} - {turno.servicio_nombre} con {turno.empleado_nombre}
                </div>
                {turno.sena_monto > 0 && (
                  <div className="mt-2 bg-yellow-200 border border-yellow-400 rounded-lg p-3 flex items-center space-x-2">
                    <CurrencyDollarIcon className="h-5 w-5 text-yellow-700" />
                    <span className="text-yellow-800 font-semibold">
                      Sena: ${turno.sena_monto.toFixed(2)} ({turno.sena_metodo_pago})
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
  
      {/* Resumen de costos */}
      <div className="bg-gray-50 border border-gray-300 rounded-xl p-6 shadow-md text-base space-y-3">
        <div className="flex justify-between">
          <span className="font-medium">Subtotal:</span>
          <span>${totalAmount.toFixed(2)}</span>
        </div>
        {surcharge > 0 && (
          <div className="flex justify-between">
            <span className="font-medium">Recargo ({selectedPaymentMethod?.valor_recargo}%):</span>
            <span>${surcharge.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-gray-800 border-t border-gray-200 pt-2">
          <span>Total con recargo:</span>
          <span>${totalWithSurcharge.toFixed(2)}</span>
        </div>
        {giftCardAmount > 0 && (
          <div className="flex justify-between text-green-800">
            <span>Descuento Gift Card:</span>
            <span>- ${giftCardAmount.toFixed(2)}</span>
          </div>
        )}
        {totalSameDaySena > 0 && (
          <div className="flex justify-between text-yellow-800">
            <span>Descuento otros turnos:</span>
            <span>- ${totalSameDaySena.toFixed(2)}</span>
          </div>
        )}
        {monto > 0 && (
          <div className="flex justify-between text-yellow-800">
            <span>Abono (Sena) turno actual:</span>
            <span>- ${monto.toFixed(2)}</span>
          </div>
        )}
      </div>
  
      {/* Total Final */}
      <div className="bg-white border border-gray-400 rounded-xl p-6 shadow-lg">
        <div className="flex justify-between items-center text-2xl font-bold text-gray-900">
          <span>Total a pagar:</span>
          <span>${totalFinal.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
  
};

export default PaymentSummary;
